import AppOptions from './options';
import CookiesDidomi from '../libs/cookies_didomi';
import Banners from '../libs/banners';
import Events from '../libs/events';
import Video from '../libs/video';
import Image from '../libs/image';
import Html from '../libs/html';
import User from '../libs/user';
import Joke from '../libs/joke';
import Epg from '../libs/epg';
import Poll from '../libs/poll';
import Gadgets from '../libs/gadgets';
import Comments from '../libs/comments';
import Observer from '../libs/observer';
import Scroll from '../libs/scroll';
import OnlWebsocket from '../libs/websocket';
import Weather from '../libs/weather';
import GQL from '../gql/gql';
import Trackers from '../libs/trackers';
import RecipeBookmarks from '../libs/recipe_bookmarks';
import SowingCalendar from '../libs/sowing_calendar';
import Reload from '../libs/reload';
import Search from '../libs/search';
import RateLimiter from '../libs/rate_limiter';

class App {
    protected gql: GQL;

    public cookies: CookiesDidomi;
    public banners: Banners;
    public events: Events;
    public observer: Observer;
    public video: Video;
    public image: Image;
    public user: User;
    public html: Html;
    public comments: Comments;
    public joke: Joke;
    public epg: Epg;
    public gadgets: Gadgets;
    public trackers: Trackers;
    public poll: Poll;
    public scroll: Scroll;
    public onlWebsocket: OnlWebsocket;
    public weather: Weather;
    public bookmarks: RecipeBookmarks;
    public sowingCalendar: SowingCalendar;
    public reload: Reload;
    public search: Search;
    public rateLimiter: RateLimiter;

    constructor(
        private options: AppOptions
    ) {
        this.options.isMobile = this.getIsMobile();

        this.gql = new GQL(options);
        this.html = new Html();
        this.rateLimiter = new RateLimiter();
        this.events = new Events(this.html);
        this.observer = new Observer(this.html, this.options);
        this.scroll = new Scroll(this.html, this.rateLimiter);
        this.image = new Image(this.html, this.options);
        this.cookies = new CookiesDidomi(this.html, this.events, this.options);
        this.gadgets = new Gadgets(this.html, this.events, this.cookies, this.options);
        this.user = new User(this.events, this.cookies, this.gql, this.html, this.options);
        this.trackers = new Trackers(this.gql, this.user, this.cookies, this.options);
        this.joke = new Joke(this.html, this.gql);
        this.epg = new Epg(this.html);
        this.poll = new Poll(this.html, this.gql, this.cookies);
        this.onlWebsocket = new OnlWebsocket(this.html, this.user, this.cookies, this.options);
        this.weather = new Weather(this.html, this.cookies, this.events);
        this.sowingCalendar = new SowingCalendar(this.html);
        this.banners = new Banners(this.cookies, this.html, this.observer, this.options);       
        this.reload = new Reload();
        this.video = new Video(this.html, this.gql, this.cookies, this.banners, this.reload, this.options);
        this.bookmarks = new RecipeBookmarks(this.html, this.gql, this.events);
        this.comments = new Comments(this.html, this.gql, this.user, this.banners, this.options);
        this.search = new Search(this.html, this.rateLimiter);
    }

    run() {
        console.debug('Starting app...');

        this.user.handleDeviceId();
        this.user.loadUserInfo()
            .then(user => {
                // Cross-site login makes no sense for 24ur.com, because
                // cookies are on the same domain.
                // Don't try to cross-site login user if user is already
                // logged in by local JWT.
                // HR has no logins.
                if (user || this.options.siteId === 1 || this.options.country === 'hr') {
                    return;
                }

                this.user.tryCrossLogin()
            });

        this.cookies.init();
        this.observer.init();
        this.gadgets.splideInit();
        this.scroll.init();
        this.trackers.pageHit();
        this.search.init();

        if (this.options.showAds) {
            this.banners.init();
        }

        setTimeout(() => {
            this.onlWebsocket.init();
            this.registerServiceWorker();
        }, 1000);

        this.events.sendEvent('started', this.options);
    }

    private registerServiceWorker(): void {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/sw.js');
        }
    }

    // Anything that has width less than 1200 is by our
    // definition 'mobile' -> shows mobile layout and mobile
    // banners. window.matchMedia does not trigger reflow.
    private getIsMobile(): boolean {
        return window.matchMedia("(max-width: 1199px)").matches;
    }
}

export default App;
